import { z } from 'zod';
import type { ShopifyStoreDiscountFilters } from '@discovr/types';

const STATUSES = ['active', 'scheduled', 'expired'] as const;
const TYPES = ['amount_off_products', 'amount_off_order', 'bxgy', 'free_shipping'] as const;
const METHODS = ['code', 'automatic'] as const;
const VIEWS = ['discovr', 'other'] as const;
// The BE rejects the all-zero id with a 400.
const CAMPAIGN_ID = /^(?!0{24}$)[0-9a-f]{24}$/i;

export type ShopifyDiscountsView = (typeof VIEWS)[number];

export interface ShopifyDiscountsSearch {
  /** Absent reads as `discovr`. */
  view?: ShopifyDiscountsView;
  /** Discovr view only. */
  campaign?: string;
  status?: (typeof STATUSES)[number];
  /** Other view only. */
  type?: (typeof TYPES)[number];
  /** Other view only. */
  method?: (typeof METHODS)[number];
  q?: string;
}

const rawSchema = z.object({
  view: z.enum(VIEWS).optional().catch(undefined),
  // Legacy `?source=`: only `other` still means something.
  source: z.enum(VIEWS).optional().catch(undefined),
  campaign: z.string().regex(CAMPAIGN_ID).optional().catch(undefined),
  status: z.enum(STATUSES).optional().catch(undefined),
  type: z.enum(TYPES).optional().catch(undefined),
  method: z.enum(METHODS).optional().catch(undefined),
  // The router JSON-parses search values, so `?q=2026` arrives as a number.
  q: z
    .union([z.string(), z.number()])
    .transform((v) => String(v).trim() || undefined)
    .optional()
    .catch(undefined),
});

/** URL state of the Store discounts page. An invalid or absent value reads as "all";
 *  filters that belong to the other tab are dropped. */
export const shopifyDiscountsSearchSchema = rawSchema.transform(
  ({ view, source, campaign, status, type, method, q }): ShopifyDiscountsSearch => {
    const other = (view ?? source) === 'other';
    return other
      ? { view: 'other', status, type, method, q }
      : { campaign, status, q };
  },
);

export const discountsView = (f: ShopifyDiscountsSearch): ShopifyDiscountsView =>
  f.view === 'other' ? 'other' : 'discovr';

export const hasActiveDiscountFilters = (f: ShopifyDiscountsSearch): boolean =>
  discountsView(f) === 'other'
    ? Boolean(f.status || f.type || f.method || f.q)
    : Boolean(f.status || f.campaign || f.q);

/** The search after changing tab: status and q carry over, the other tab's filters do not. */
export const switchDiscountsView = (
  prev: ShopifyDiscountsSearch,
  view: ShopifyDiscountsView,
): ShopifyDiscountsSearch => ({
  view: view === 'other' ? 'other' : undefined,
  status: prev.status,
  q: prev.q,
});

/** The list query for the tab in the URL. */
export const toDiscountFilters = (f: ShopifyDiscountsSearch): ShopifyStoreDiscountFilters => {
  const common = { ...(f.status && { status: f.status }), ...(f.q && { q: f.q }) };
  return discountsView(f) === 'other'
    ? { source: 'other', ...common, ...(f.type && { type: f.type }), ...(f.method && { method: f.method }) }
    : { source: 'discovr', ...common, ...(f.campaign && { campaign_id: f.campaign }) };
};
